import React, { memo, useCallback } from 'react';
import { HERO_CONFIG } from '../config/constants';
import VideoElement from './VideoElement';

const { VIDEOS, FALLBACK_IMAGES } = HERO_CONFIG;

const VideoManager = ({
  videoRefs,
  heroState,
  updateHeroState,
  startTransition,
  createHandleLoadedMetadata,
}) => {
  // 動画コンテンツのレンダリング
  const renderContent = useCallback(() => {
    return VIDEOS.map((src, index) => {
      const isActive = index === heroState.currentIndex;
      const opacity = isActive ? 1 : 0;
      // 動画が読み込まれているかどうかを確認
      const isVideoLoaded = heroState.videosLoaded;
      // 最初の動画または現在アクティブな動画のみ表示
      const shouldShow = isVideoLoaded && (index === 0 || isActive);
      // エラーが発生しているかどうか
      const hasError = heroState.videoErrors[index];

      return (
        <VideoElement
          key={src}
          ref={videoRefs.current[index]}
          src={shouldShow && !hasError ? src : undefined}
          isVisible={shouldShow}
          index={index}
          muted
          playsInline
          preload={index === 0 ? 'metadata' : 'none'}
          loop={false}
          fallbackImage={FALLBACK_IMAGES[0]}
          onCanPlay={() =>
            process.env.NODE_ENV === 'development' &&
            console.log(`Video ${index + 1} can play`)
          }
          onLoadStart={() =>
            process.env.NODE_ENV === 'development' &&
            console.log(`Video ${index + 1} loading started`)
          }
          onError={e => {
            if (process.env.NODE_ENV === 'development') {
              console.error(`Video ${index + 1} error:`, e);
            }
            // エラーを記録
            updateHeroState(prev => {
              const newErrors = [...(prev.videoErrors || [])];
              newErrors[index] = true;
              return { ...prev, videoErrors: newErrors };
            });
          }}
          onLoadedMetadata={createHandleLoadedMetadata(index)}
          onEnded={() => {
            if (process.env.NODE_ENV === 'development') {
              console.log(
                `Video ${index + 1} ended, currentIndex: ${heroState.currentIndex}`
              );
            }
            if (index === heroState.currentIndex) {
              if (process.env.NODE_ENV === 'development') {
                console.log(`Video ${index + 1} ended, triggering transition`);
              }
              startTransition();
            }
          }}
          onSeeked={() => {
            if (process.env.NODE_ENV === 'development') {
              console.log(
                `Video ${index + 1} seeked to: ${videoRefs.current[index].current?.currentTime.toFixed(1)}s`
              );
            }
          }}
          onTimeUpdate={() => {
            if (
              process.env.NODE_ENV === 'development' &&
              index === heroState.currentIndex
            ) {
              const video = videoRefs.current[index].current;
              if (video && video.currentTime < 1.0) {
                console.log(
                  `Video ${index + 1} time update: ${video.currentTime.toFixed(1)}s (possible seek detected)`
                );
              }
            }
          }}
          style={{
            opacity: opacity,
            transition: 'opacity 1s ease-in-out',
            zIndex: isActive ? 2 : 0,
          }}
        />
      );
    });
  }, [
    heroState.currentIndex,
    heroState.videosLoaded,
    heroState.videoErrors,
    startTransition,
    updateHeroState,
    createHandleLoadedMetadata,
    videoRefs,
  ]);

  return <>{renderContent()}</>;
};

export default memo(VideoManager);
